"use client";

import { AskSabine } from "@/components/nav/CoachLauncher";
import HelpButton from "@/components/nav/HelpButton";
import { NavPanelShell } from "@/components/nav/NavPanel";
import NavSearchRow from "@/components/nav/NavSearchRow";
import StudioBand from "@/components/nav/StudioBand";
import {
  NAV_ARRIVE,
  NAV_COLUMN_WIDTH,
  NAV_INK,
  NAV_TOP_BAR_HEIGHT,
  arriveStep,
} from "@/components/nav/nav-styles";
import { cn } from "@/lib/utils";

/**
 * The navy bar across the top of a wide screen, above the rail and the page:
 * the studio's mark and the search field at the left, Help and Ask Sabine at
 * the right. It stays put while the page scrolls.
 *
 * Sabine's chat opens in a column at the right edge of the screen, and her
 * header sits in the bar right above it, at its width (rendered by the chat
 * into `chatHeaderSlotRef`), where the launcher was. The slot is a
 * NavPanelShell of its own, always mounted, so it comes and goes with the
 * column below: it fades and narrows as the column closes rather than
 * vanishing ahead of it, and Help moves over with it.
 *
 * @param {object} props
 * @param {object} props.studio  StudioBand's props
 * @param {object} props.search  NavSearchRow's props
 * @param {{ placeholder: string, onOpen: () => void } | null} props.ask  The
 *   page's coach; null when the page registered none
 * @param {{ open: boolean, onClick: () => void } | null} props.help
 * @param {boolean} props.chatOpen
 * @param {boolean} [props.chatInstant]  The chat opened with the page: its
 *   header is drawn in place, like the column (NavCoachPane)
 * @param {(node: HTMLElement | null) => void} props.chatHeaderSlotRef
 * @param {boolean} [props.inert]
 */
export default function NavTopBar({
  studio,
  search,
  ask,
  help,
  chatOpen,
  chatInstant,
  chatHeaderSlotRef,
  inert,
}) {
  return (
    <header
      inert={inert}
      data-app-nav
      data-app-top-bar
      className={cn(
        "sticky top-0 z-40 hidden shrink-0 items-center md:flex print:hidden",
        NAV_TOP_BAR_HEIGHT,
        NAV_INK,
        // Her header runs to the bar's edge, over the column below.
        chatOpen ? "pl-4" : "px-4",
      )}
    >
      <StudioBand {...studio} />
      <div className="ml-3 flex min-w-0 flex-1">
        <NavSearchRow
          open={search.open}
          buttonRef={search.buttonRef}
          inputSlotRef={search.inputSlotRef}
          onOpen={search.onOpen}
        />
      </div>
      <div className="ml-3 flex shrink-0 items-center gap-3">
        {help && <HelpButton open={help.open} onClick={help.onClick} />}
        {ask && !chatOpen && (
          // Keyed so it comes in again when the chat closes.
          <div key="launcher" className={cn(NAV_ARRIVE, "flex min-w-0")} style={arriveStep(1)}>
            <AskSabine placeholder={ask.placeholder} onOpen={ask.onOpen} ink />
          </div>
        )}
      </div>
      <div className="flex self-stretch">
        <NavPanelShell open={chatOpen} instant={chatInstant} width={NAV_COLUMN_WIDTH}>
          <div
            ref={chatHeaderSlotRef}
            className={cn(NAV_ARRIVE, "my-auto")}
            style={arriveStep(0)}
          />
        </NavPanelShell>
      </div>
    </header>
  );
}
